import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DarkTheme, DefaultTheme, PAGE_MENU_DRAWER_WIDTH } from '@wheeebo/polaris-ui';
const APPBAR_HEIGHT = 48;
const CESIUM_VIEWER_TOOLBAR_DEFAULT_TOP_PX = 4;
const CESIUM_INFOBOX_DEFAULT_TOP_PX = 38;
const CESIUM_VIEWER_ANIMATION_CONTAINER_DEFAULT_LEFT_PX = 0;
const CESIUM_VIEWER_TIMELINE_CONTAINER_DEFAULT_LEFT_PX = 169;
const cesiumIframeStyle = `<style id="polaris-theme">
body[data-theme=light] .cesium-infoBox-description table {
  width: 100%;
  font-family: serif;
  color: ${DefaultTheme.palette.text.primary};
}
body[data-theme=light] .cesium-infoBox-description table tr:nth-child(odd) {
  background-color: ${DefaultTheme.palette.action.hover};
}
.body[data-theme=dark] .cesium-infoBox-description table {
  width: 100%;
  font-family: serif;
  color: ${DarkTheme.palette.text.primary};
}
.body[data-theme=dark] .cesium-infoBox-description table tr:nth-child(odd) {
  background-color: ${DarkTheme.palette.action.hover};
}
.cesium-infoBox-description table tr td {
  padding-left: 1rem;
  padding-right: 1rem;
}
</style>`;
function findCesiumIframeDocument() {
    return document.querySelector('iframe.cesium-infoBox-iframe')?.contentWindow?.document;
}
const propOptions = ['isDrawerOpen', 'infoBoxLeftPadding'];
const CesiumContainer = styled(Box, {
    shouldForwardProp: (prop) => !propOptions.includes(prop.toString()),
})(({ theme, isDrawerOpen, infoBoxLeftPadding }) => {
    const fontColor = theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.54)' : 'white';
    const leftSize = isDrawerOpen ? PAGE_MENU_DRAWER_WIDTH : parseInt(theme.spacing(7)) + 1;
    const colorModeChanged = React.useCallback((paletteMode) => {
        const body = findCesiumIframeDocument()?.body;
        if (body) {
            body.dataset['theme'] = paletteMode;
        }
    }, []);
    const iframeDocument = findCesiumIframeDocument();
    const style = iframeDocument?.querySelector('#polaris-theme');
    if (!style) {
        iframeDocument?.head?.insertAdjacentHTML('beforeend', cesiumIframeStyle);
        colorModeChanged(theme.palette.mode);
    }
    React.useEffect(() => colorModeChanged(theme.palette.mode), [colorModeChanged, theme.palette.mode]);
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        '& .cesium-viewer-toolbar': {
            top: `${APPBAR_HEIGHT + CESIUM_VIEWER_TOOLBAR_DEFAULT_TOP_PX}px !important`,
            position: 'absolute',
            left: '50%',
            width: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 600,
            '& .cesium-viewer-geocoderContainer': {
                display: 'block',
            },
            '& .cesium-geocoder-input': {
                borderRadius: '4px',
                padding: '5px 32px 5px 8px',
                width: '100%',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.paper,
            },
            '& .cesium-geocoder-searchButton': {
                padding: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
            },
            '& .cesium-svgPath-svg': {
                width: '24px',
                height: '24px',
                position: 'relative',
                fill: fontColor,
            },
        },
        '& .cesium-infoBox': {
            top: `${APPBAR_HEIGHT + CESIUM_INFOBOX_DEFAULT_TOP_PX}px !important`,
            left: `${leftSize + (infoBoxLeftPadding || 4)}px !important`,
            maxWidth: '380px',
            backgroundColor: theme.palette.background.paper,
            borderWidth: 0,
            boxShadow: 'none',
            borderRadius: '4px',
            '& .cesium-infoBox-title': {
                fontFamily: 'serif',
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
            },
            '& .cesium-button': {
                color: theme.palette.text.primary,
                borderWidth: 0,
                backgroundColor: 'transparent',
                '& .cesium-svgPath-svg': {
                    fill: fontColor,
                },
            },
            '& .cesium-infoBox-close': {
                color: fontColor,
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            },
        },
        '& .cesium-viewer-animationContainer': {
            left: `${leftSize + CESIUM_VIEWER_ANIMATION_CONTAINER_DEFAULT_LEFT_PX}px !important`,
        },
        '& .cesium-viewer-timelineContainer': {
            left: `${leftSize + CESIUM_VIEWER_TIMELINE_CONTAINER_DEFAULT_LEFT_PX}px !important`,
        },
        '& .cesium-viewer-bottom': {
            left: 'auto !important',
            right: 4,
        },
    };
});
export default React.memo(CesiumContainer);
